import { trpc, trpcApi } from '@bitbot/api';
import { createGlobalStore } from 'hox';
import { groupBy, keyBy } from 'lodash';
import { useEffect, useState, useRef } from 'react';
import { useToken } from '../user/token';
import { Notify } from '@bitbot/ui-design';
import { useLocalStorageState } from 'ahooks';
import { useGlobalValueStore } from '../trade/common';

export const TOTAL_WALLET_KEY = 'TOTAL_WALLET';

export const config = {
  base: [
    {
      tokenAddress: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      isShow: true,
    },
    {
      tokenAddress: '0x252D223D0550BC6c137B003D90bc74F5341A2818',
      isShow: false,
    },
  ],
};

type SingleConfig = {
  tokenAddress: string;
  isShow: boolean;
};

type AsseConfigType = {
  [prop: string]: SingleConfig[];
};

type chainName = 'base';

type wallets = Awaited<
  ReturnType<typeof trpcApi.wallet.getWallets.query>
>['list'];
type walletsInfo = Awaited<
  ReturnType<typeof trpcApi.wallet.getWallets.query>
>;
export const [useWallets] = createGlobalStore(() => {
  const { hasLogin, setTradeToken, hasTradeLogin } = useToken();
  const {
    currentWalletAddress,
    setCurrentWalletAddress,
    setRecentWallets,
  } = useGlobalValueStore();
  const [chain, setChain] = useState<{
    name: chainName;
    icon: string;
    baseSymbol: string;
    baseSymbolAddress: string;
  }>({
    name: 'base',
    icon: '/chain/base.svg',
    baseSymbol: 'ETH',
    baseSymbolAddress: '0x4200000000000000000000000000000000000006',
  });
  const { data: mainTokenPriceUsd } =
    trpc.wallet.getTokenPriceUsd.useQuery(
      {
        tokenAddress: chain?.baseSymbolAddress!,
        chain: chain.name,
      },
      {
        enabled: !!chain?.baseSymbolAddress,
      },
    );
  const [baseAssetConfig, setBaseAssetConfig] =
    useLocalStorageState<AsseConfigType>('base-asset-config', {
      defaultValue: {},
    });
  // Undefined -> All wallet
  const [currentWallet, setCurrentWallet] = useState<string | undefined>(
    currentWalletAddress,
  );

  const [walletCache, setWalletCache] =
    useLocalStorageState<wallets>('walletData');

  const [wallets, setWallets] = useState<wallets>(walletCache ?? []);
  const [walletsInfo, setWalletsInfo] = useState<walletsInfo>();
  const [initingWalletInfo, setInitingWalletInfo] = useState(true);
  const retryGetWallets = useRef(0);
  const getWallets = (
    needLogin: boolean,
    onSuccess?: (isOtpEnabled: boolean) => void,
    onFail?: () => void,
  ) => {
    trpcApi.wallet.getWallets
      .query({ chain: chain.name, needLogin })
      .then(data => {
        retryGetWallets.current = 0;
        if (needLogin) {
          if (!data.isOtpEnabled) {
            setCurrentWalletAddress();
          } else {
            if (data.token) {
              setTradeToken(data.token);
            }
            setWalletsInfo(data);
          }
          setInitingWalletInfo(false);
        } else {
          setInitingWalletInfo(false);
          setWalletsInfo(data);
        }
        if (onSuccess) {
          onSuccess(data.isOtpEnabled);
        }
      })
      .catch(error => {
        if (error.message.includes("can't find knightsafe user")) {
          removeLoginCache();
          setInitingWalletInfo(false);
          return;
        }
        if (error.message.includes('not found tradingToken')) {
          removeLoginCache();
          getWallets(true);
          return;
        }
        if (needLogin) {
          Notify.error(error.message);
          setInitingWalletInfo(false);
        } else {
          retryGetWallets.current++;
          if (retryGetWallets.current >= 3) {
            removeLoginCache();
            setInitingWalletInfo(false);
          } else {
            getWallets(needLogin);
          }
        }
        if (onFail) {
          onFail();
        }
      });
  };

  const _setCurrentWallet = (address: string | undefined) => {
    setCurrentWallet(address);
    setCurrentWalletAddress(address);
  };

  const removeLoginCache = () => {
    setWalletsInfo(undefined);
    setWallets([]);
    setWalletCache(undefined);
    setTradeToken(undefined);
    _setCurrentWallet(undefined);
    setRecentWallets([]);
  };
  useEffect(() => {
    if (hasLogin) {
      trpcApi.wallet.getWallets
        .query({
          chain: chain.name,
          needLogin: !hasTradeLogin,
        })
        .then(data => {
          setInitingWalletInfo(false);
          if (!data.isOtpEnabled) {
            //清空之前的loginCache
            removeLoginCache();
          } else {
            if (data.token) {
              setTradeToken(data.token);
            }
            setWalletsInfo(data);
          }
        })
        .catch(error => {
          if (
            error.message.includes("can't find knightsafe user") ||
            error.message.includes('User not found')
          ) {
            //清空之前的loginCache
            removeLoginCache();
            setInitingWalletInfo(false);
          } else if (
            error.message.includes('invalid tradingToken') ||
            error.message.includes('not found tradingToken')
          ) {
            //清空之前的loginCache
            removeLoginCache();
            getWallets(true);
          } else if (error.message.includes('noInternetConnection')) {
            if (!!hasTradeLogin) {
              removeLoginCache();
              getWallets(true);
            } else {
              getWallets(false);
            }
          } else {
            getWallets(false);
          }
        });
    }
  }, [hasLogin]);

  useEffect(() => {
    if (!!walletsInfo) {
      setWallets(walletsInfo.list);
      setWalletCache(walletsInfo.list);
      if (!walletsInfo.isOtpEnabled) {
        return;
      }
      if (walletsInfo?.token) {
        setTradeToken(walletsInfo.token);
      }
      if (walletsInfo.list.length > 0) {
        if (
          !walletsInfo.list.find(
            item => item.address === currentWalletAddress,
          )
        ) {
          setCurrentWallet(walletsInfo.list[0].address);
          setCurrentWalletAddress(walletsInfo.list[0].address);
        } else {
          setCurrentWallet(currentWalletAddress);
        }
      } else {
        if (walletsInfo.list.length === 0) {
          setCurrentWalletAddress('');
        }
      }
    }
  }, [walletsInfo]);

  const totalValueUSD = walletsInfo?.totalUSDVal ?? 0;

  const walletsByChain = Object.entries(
    groupBy(wallets, w => w.chain),
  ).map(([chain, wallets]) => ({ chain, wallets }));

  const currentWalletStats = wallets.find(
    w => w.address === currentWallet,
  );

  const walletAssetConfig =
    baseAssetConfig && currentWallet ? baseAssetConfig[currentWallet] : [];
  const walletAssetConfigObj = keyBy(walletAssetConfig, 'tokenAddress');
  return {
    wallets,
    walletsByChain,
    totalValueUSD,
    currentWalletStats,
    currentWallet,
    setCurrentWallet: _setCurrentWallet,
    objWallets: keyBy(wallets, obj => obj.address),
    chain,
    walletAssetConfig, //当前钱包的asset config数组
    walletAssetConfigObj, //当前钱包的asset config obj对象
    setBaseAssetConfig,
    baseAssetConfig, //当前用户所有钱包的asset config
    getWallets,
    walletsInfo,
    setWalletsInfo,
    setWallets,
    setWalletCache,
    mainTokenPriceUsd,
    initingWalletInfo,
  };
});

export type WalletItem = ReturnType<typeof useWallets>['wallets'][number];
